import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { designSystem, Modal, Panel, PanelGroup } from '@yola/ws-ui';
import { customization, blockTemplates } from '@yola/ws-sdk';
import bowser from 'yola-bowser';
import Breadcrumbs from 'src/js/modules/common/components/breadcrumbs';
import ViewWithStaticAssets from '../../blocks/components/view-with-static-assets';
import compileCustomTemplate from '../helpers/compile-custom-template';
import generatePresetLabel from '../helpers/generate-preset-label';
import constants from '../constants';
import ContentBox from './content-box';
import ButtonPreset from './button-preset';

const {
  Stack,
  DialogHeader,
  DialogButton,
  DialogButtonGroup,
  DialogButtonGroupSeparator,
  Box,
  Heading,
  Paragraph,
} = designSystem;

const { DEFAULT_OFFSET_X, DEFAULT_OFFSET_Y, DEFAULT_DIALOG_HEIGHT, DEFAULT_DIALOG_WIDTH } =
  constants;

const BUTTON_PRESET_PREVIEW_BLOCK_TEMPLATE_ID = 'button-preset-preview';

const DIALOG_MIN_HEIGHT = 255;

const DesignButtonsDialog = ({
  presets,
  activePresetId,
  activePaletteId,
  activeFontId,
  captions,
  onChange,
  onCancel,
  onSubmit,
}) => {
  const buttonPresetPreviewTemplate = useSelector(
    customization.selectors.getButtonPresetPreviewTemplate
  );
  const blockTemplate = useSelector((state) =>
    blockTemplates.selectors.getBlockTemplateById(state, BUTTON_PRESET_PREVIEW_BLOCK_TEMPLATE_ID)
  );

  const customizations = {
    globalOffsetTop: '0px',
    globalOffsetBottom: '0px',
  };
  const dependencies = useMemo(
    () => ({
      js: [],
      css: [],
      ...blockTemplate.dependencies,
    }),
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
    []
  );

  return (
    <Modal
      draggable
      fullscreen={bowser.mobile}
      width={DEFAULT_DIALOG_WIDTH}
      minWidth={DEFAULT_DIALOG_WIDTH}
      maxWidth={DEFAULT_DIALOG_WIDTH}
      height={DEFAULT_DIALOG_HEIGHT}
      minHeight={DIALOG_MIN_HEIGHT}
      offsetX={DEFAULT_OFFSET_X}
      offsetY={DEFAULT_OFFSET_Y}
      dragHandleSelector=".ws-drag-trigger"
      dragCancelSelector=".ws-ui-dialog-header-controls"
      overlay="none"
      resizeIcon="ns-resize"
      handleCancel={onCancel}
      handleSubmit={onSubmit}
      onOverlayClick={onSubmit}
      className="ws-design-buttons-dialog"
    >
      <PanelGroup height="100%">
        <Panel
          align="middle"
          corners="squared"
          className="ws-drag-trigger"
          style={{ flexShrink: '0' }}
        >
          <DialogHeader paddingBottom="spacing-s">
            <Box>
              <Box paddingBottom="spacing-2-xs">
                <Breadcrumbs breadcrumbs={captions.breadcrumbs} />
              </Box>
              <Box>
                <Stack>
                  <Heading type="heading-3">{captions.title}</Heading>
                  <Paragraph>{captions.description}</Paragraph>
                </Stack>
              </Box>
            </Box>
          </DialogHeader>
        </Panel>
        <Panel
          height="100%"
          scrollable
          style={{ borderBottom: 'none' }}
          className="ws-design-buttons-dialog__content-wrapper"
        >
          <div className="ws-design-buttons-dialog__content">
            <ViewWithStaticAssets
              customization={customizations}
              dependencies={dependencies}
              forcedScale={1}
              forcedColorPaletteId={activePaletteId}
              forcedFontPairId={activeFontId}
            >
              <ContentBox>
                <Stack gap="spacing-xs">
                  {presets.map((preset) => {
                    const extendedPreset = {
                      variables: {
                        ...preset.variables,
                        primary_button_label: `${captions.presetButtonLabel} 1`,
                        secondary_button_label: `${captions.presetButtonLabel} 2`,
                      },
                    };
                    const previewHTML = compileCustomTemplate(
                      buttonPresetPreviewTemplate,
                      extendedPreset
                    );

                    return (
                      <ButtonPreset
                        key={preset.id}
                        id={preset.id}
                        isPremium={Boolean(preset.premium)}
                        title={generatePresetLabel(captions.presetLabel, preset.id)}
                        contentHTML={previewHTML}
                        isSelected={preset.id === activePresetId}
                        onClick={onChange}
                      />
                    );
                  })}
                </Stack>
              </ContentBox>
            </ViewWithStaticAssets>
          </div>
        </Panel>
        <Panel corners="squared" style={{ flexShrink: '0' }}>
          <DialogButtonGroup>
            <DialogButton onClick={onSubmit}>{captions.submitButton}</DialogButton>
            <DialogButtonGroupSeparator />
            <DialogButton onClick={onCancel}>{captions.cancelButton}</DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
    </Modal>
  );
};

DesignButtonsDialog.propTypes = {
  presets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      variables: PropTypes.shape().isRequired,
    })
  ).isRequired,
  activePresetId: PropTypes.string.isRequired,
  activePaletteId: PropTypes.string.isRequired,
  activeFontId: PropTypes.string.isRequired,
  captions: PropTypes.shape({
    breadcrumbs: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    description: PropTypes.string,
    submitButton: PropTypes.string,
    cancelButton: PropTypes.string,
    presetLabel: PropTypes.string,
    presetButtonLabel: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default DesignButtonsDialog;
